import {
    FINANCE,
    LEASE,
    CASH,
    MongoDealer,
    MongoDealerCreditTier,
    IVehicleForPayment,
    IPaymentOverride,
    IPaymentTradeIn,
    IMetadataWithDealerId,
    RequireAtLeastOne,
    IVehicleIdentifier,
    IPaymentOverrideFinance,
    IPaymentOverrideCash,
    IPaymentOverrideLease,
    RequestType,
    PaymentProcessingStrategies,
    ShopperAddress
} from '@makemydeal/dr-platform-types';
import { RequestedPaymentTypeError } from './errors';
import {
    IPaymentServicesRequestPayload,
    IPaymentServicesRequestCash,
    IPaymentServicesRequestLease,
    IPaymentServicesRequestFinance
} from './types';
import * as utils from './utils';

const isSet = <T>(value: T | void): value is T => value !== undefined;

const EMPTY_INCENTIVES = {
    conditional: []
};

const isShopperAddressValid = (shopper: ShopperAddress) => {
    return shopper.state && shopper.city && shopper.streetAddress;
}

export const buildPaymentPayload = (
    dealerId: number,
    dealer: MongoDealer,
    vehicle: IVehicleForPayment,
    payment: IPaymentOverride | undefined,
    traceId: string,
    shopperZip: string,
    metadata: IMetadataWithDealerId,
    tradeIn?: IPaymentTradeIn,
    partnerDealerId?: number | string,
    partnerId?: string,
    useAliasSettings?: boolean,
    shopper?: ShopperAddress
): IPaymentServicesRequestPayload => {
    const { actionForPayment, sponsor, source, writeToCache, readFromCache } = metadata;

    const vehicleIdentifier = {
        vin: vehicle.vin,
        oemCode: vehicle.oemCode,
        enhancedAlgCode: vehicle.enhancedAlgCode,
        chromeStyleId: vehicle.chromeStyleId
    } as RequireAtLeastOne<IVehicleIdentifier>;

    const {
        condition,
        listedPrice,
        mileage,
        retailPrice,
        year,
        bodyStyle,
        trim,
        packageCode,
        engineCode,
        destinationCharge,
        fuelType,
        invoiceAmount,
        unladenWeight
    } = vehicle;

    const isNewVehicle = utils.isVehicleNew(vehicle);
    const defaultCreditTier = utils.getDealerDefaultTier(dealer, isNewVehicle);
    const creditScore = payment?.creditScore ?? utils.getAverageCreditScore(defaultCreditTier);

    const includeTaxes = payment?.includeTaxes ?? utils.isTaxesIncluded(dealer);
    const includeGovFees = payment?.includeGovFees ?? utils.getIncludeGovernmentFees(dealer);

    const incentivesPreferences = payment?.incentivesPreferences;

    const requestTypes = payment?.requestTypes;

    const dealerZipCode = dealer.address.zip;
    const zipCode = shopperZip || dealerZipCode;

    let quotesFiltrationStrategy = payment?.quotesFiltrationStrategy ?? utils.getQuotesFiltrationStrategy(dealer);
    const {
        bypassDraasSettingsCache,
        includeIncentives,
        locationId,
        enableRTSCalculation,
        useGenericCalcs,
        includeDefaultGovFees,
        includeDefaultLenderFees,
        includeDefaultDealerFees,
        includeDefaultDealerBasedTaxes
    } = payment ?? {};
    if (useGenericCalcs === true) {
        quotesFiltrationStrategy = PaymentProcessingStrategies.generic;
    }
    const paymentServicesPayload: IPaymentServicesRequestPayload = {
        vehicle: {
            ...vehicleIdentifier,
            condition,
            mileage,
            retailPrice: retailPrice || listedPrice,
            year,
            invoiceAmount,
            ...(bodyStyle !== undefined && { bodyStyle }),
            ...(trim !== undefined && { trim }),
            ...(packageCode !== undefined && { packageCode }),
            ...(engineCode !== undefined && { engineCode }),
            ...(destinationCharge !== undefined && { destinationCharge }),
            ...(fuelType !== undefined && { fuelType }),
            ...(unladenWeight !== undefined && { unladenWeight })
        },
        preferences: {
            creditScore,
            dealerId,
            partnerDealerId: partnerDealerId ?? dealerId,
            partnerId: partnerId ?? 'MMD',
            useAliasSettings: useAliasSettings ?? false,
            dealerZipCode,
            enableRTSCalculation: true,
            includeIncentives: true,
            includeGovFees,
            includeTaxes,
            zipCode,
            ...((shopper && isShopperAddressValid(shopper)) && {
                shopper: {
                    state: shopper.state,
                    city: shopper.city,
                    streetAddress: shopper.streetAddress
                }
            }),
            quotesFiltrationStrategy,
            useDefaultLocationToCalculatePayment: true,
            ...(isSet(bypassDraasSettingsCache) && { bypassDraasSettingsCache }),
            ...(isSet(includeIncentives) && { includeIncentives }),
            ...(isSet(enableRTSCalculation) && { enableRTSCalculation }),
            ...(locationId !== undefined && { locationId }),
            ...(source && { source }),
            ...(isSet(writeToCache) && { writeToCache }),
            ...(isSet(readFromCache) && { readFromCache }),
            ...(incentivesPreferences !== undefined && { incentivesPreferences }),
            ...(isSet(useGenericCalcs) && { useGenericCalcs }),
            ...(isSet(includeDefaultGovFees) && { includeDefaultGovFees }),
            ...(isSet(includeDefaultLenderFees) && { includeDefaultLenderFees }),
            ...(isSet(includeDefaultDealerFees) && { includeDefaultDealerFees }),
            ...(isSet(includeDefaultDealerBasedTaxes) && { includeDefaultDealerBasedTaxes })
        },
        metadata: {
            traceId,
            actionForPayment,
            ...(sponsor !== undefined && { sponsor })
        },
        ...(tradeIn !== undefined && { tradeIn })
    };

    const allowedRequestTypes = resolveAllowedRequestTypes(requestTypes, dealer, vehicle, isNewVehicle);

    if (allowedRequestTypes[RequestType.CASH]) {
        paymentServicesPayload.cash = buildCashPayload(dealer, vehicle, payment?.cash);
    }

    if (allowedRequestTypes[RequestType.FINANCE]) {
        paymentServicesPayload.finance = buildFinancePayload(dealer, vehicle, payment?.finance, {
            creditScore,
            defaultCreditTier
        });
    }

    if (allowedRequestTypes[RequestType.LEASE]) {
        paymentServicesPayload.lease = buildLeasePayload(dealer, vehicle, payment?.lease);
    }

    return paymentServicesPayload;
};

function resolveAllowedRequestTypes(
    requestTypes: RequestType[] | undefined,
    dealer: MongoDealer,
    vehicle: IVehicleForPayment,
    isNewVehicle: boolean
): Record<RequestType, boolean> {
    const allowedRequestTypes: Record<RequestType, boolean> = {
        [RequestType.CASH]: false,
        [RequestType.FINANCE]: false,
        [RequestType.LEASE]: false
    };
    const defaultRequestTypes: RequestType[] = [RequestType.CASH, RequestType.FINANCE, RequestType.LEASE];

    /**
     * requestTypes is optional request field.
     * If nothing is specified in the requestTypes array (or it's an empty array)
     * then we return everything according to dealer settings
     */
    const requestTypesToInclude: RequestType[] =
        requestTypes === undefined || requestTypes.length === 0 ? defaultRequestTypes : requestTypes;

    for (const requestType of requestTypesToInclude) {
        if (requestType === RequestType.CASH) {
            const shouldIncludeCash = utils.getShouldIncludeCash(dealer, isNewVehicle);
            allowedRequestTypes[RequestType.CASH] = shouldIncludeCash;
        }

        if (requestType === RequestType.FINANCE) {
            allowedRequestTypes[RequestType.FINANCE] = true;
        }

        if (requestType === RequestType.LEASE) {
            const shouldIncludeLease = utils.getShouldIncludeLease(dealer, vehicle);
            allowedRequestTypes[RequestType.LEASE] = shouldIncludeLease;
        }
    }

    if (Object.values(allowedRequestTypes).every((requestType) => !requestType)) {
        throw new RequestedPaymentTypeError();
    }

    return allowedRequestTypes;
}

function buildFinancePayload(
    dealer: MongoDealer,
    vehicle: IVehicleForPayment,
    override: IPaymentOverrideFinance = {},
    credit: {
        creditScore: number;
        defaultCreditTier: MongoDealerCreditTier;
    }
): IPaymentServicesRequestFinance {
    const { listedPrice: vehicleListedPrice } = vehicle;
    const isNewVehicle = utils.isVehicleNew(vehicle);

    const listedPrice = override.offerPrice ?? vehicleListedPrice;
    const excludeStdIncs = override.excludeStdIncs ?? utils.getExcludeStandardCashIncentives(dealer, FINANCE);
    const excludeSpecialAprPrograms =
        override.excludeSpecialAprPrograms ?? utils.getExcludeSpecialAprPrograms(dealer, isNewVehicle, FINANCE);

    const downPayment = override.downPayment ?? utils.getFinanceDownPayment(isNewVehicle, listedPrice, dealer);

    const termList =
        override.termList ?? utils.getFinanceTermsList(dealer, isNewVehicle, credit.creditScore, credit.defaultCreditTier);
    const preferredTerm = utils.getFinancePreferredTerm(dealer, isNewVehicle);
    const { incentives = EMPTY_INCENTIVES, menuSelectionsId, menuItems, overrides, daysToFirstPayment } = override;

    return {
        excludeStdIncs,
        excludeSpecialAprPrograms,
        downPayment,
        listedPrice,
        termList,
        ...(preferredTerm !== undefined && { preferredTerm }),
        ...(menuSelectionsId !== undefined && { menuSelectionsId }),
        ...(menuItems !== undefined && { menuItems }),
        incentives,
        ...(overrides !== undefined && { overrides }),
        daysToFirstPayment
    };
}

function buildLeasePayload(
    dealer: MongoDealer,
    vehicle: IVehicleForPayment,
    override: IPaymentOverrideLease = {}
): IPaymentServicesRequestLease {
    const isNewVehicle = utils.isVehicleNew(vehicle);

    const listedPrice = override.offerPrice ?? utils.getLeaseOfferPrice(dealer, vehicle);

    const termList = override.termList ?? utils.getLeaseTermsList(dealer);
    const preferredTerm = utils.getLeasePreferredTerm(dealer);

    const mileageList = utils.getLeaseMileageList(dealer);

    const downPayment = override.downPayment ?? utils.getLeaseDownPayment(listedPrice, dealer);

    const excludeStdIncs = override.excludeStdIncs ?? utils.getExcludeStandardCashIncentives(dealer, LEASE);
    const annualMiles = utils.getAnnualMiles(dealer, override.annualMiles);
    const deductDueAtSigning = override.deductDueAtSigning ?? utils.getIsDueAtSigninAmountDeduct(dealer);
    const excludeSpecialAprPrograms =
        override.excludeSpecialAprPrograms ?? utils.getExcludeSpecialAprPrograms(dealer, isNewVehicle, LEASE);
    const includeMultipleSecurityDeposits =
        override.includeMultipleSecurityDeposits ?? Boolean(dealer.leasing?.allowMultipleSecurityDepositPayments);
    const { incentives = EMPTY_INCENTIVES, menuSelectionsId, menuItems, overrides, daysToFirstPayment } = override;

    return {
        annualMiles,
        deductDueAtSigning,
        downPayment,
        excludeStdIncs,
        excludeSpecialAprPrograms,
        includeMultipleSecurityDeposits,
        listedPrice,
        termList,
        preferredTerm,
        mileageList,
        ...(menuSelectionsId !== undefined && { menuSelectionsId }),
        ...(menuItems !== undefined && { menuItems }),
        incentives,
        ...(overrides !== undefined && { overrides }),
        daysToFirstPayment
    };
}

function buildCashPayload(
    dealer: MongoDealer,
    vehicle: IVehicleForPayment,
    override: IPaymentOverrideCash = {}
): IPaymentServicesRequestCash {
    const excludeStdIncs = override.excludeStdIncs ?? utils.getExcludeStandardCashIncentives(dealer, CASH);
    const listedPrice = override.offerPrice ?? utils.getCashOfferPrice(dealer, vehicle);
    const { incentives = EMPTY_INCENTIVES, menuSelectionsId, menuItems, overrides } = override;

    return {
        excludeStdIncs,
        listedPrice,
        incentives,
        ...(menuSelectionsId !== undefined && { menuSelectionsId }),
        ...(menuItems !== undefined && { menuItems }),
        ...(overrides !== undefined && { overrides })
    };
}
